import { z } from "zod";

/**
 * Closed set of Catalyst instrumentation modes the CLI can request.
 * Shared so the CLI, tRPC input schema, and analytics properties agree.
 */
export const CLI_INSTRUMENTATION_MODES = [
  "gateway",
  "tracing",
  "both",
] as const;

export type CliInstrumentationMode = (typeof CLI_INSTRUMENTATION_MODES)[number];

/**
 * Closed set of coding-agent binaries `inf instrument` can launch.
 * Shared so the CLI, tRPC input schema, and analytics properties agree.
 */
export const CLI_INSTRUMENTATION_AGENTS = [
  "claude",
  "opencode",
  "codex",
] as const;

export type CliInstrumentationAgent =
  (typeof CLI_INSTRUMENTATION_AGENTS)[number];

export const cliInstrumentationModeSchema = z.enum(CLI_INSTRUMENTATION_MODES);
export const cliInstrumentationAgentSchema = z.enum(CLI_INSTRUMENTATION_AGENTS);

/**
 * Shape of the payload the `inf` CLI sends to the telemetry.recordCliCommand
 * mutation. This type is intentionally permissive about optional enrichment
 * fields — older CLI versions may omit fields that newer ones send.
 *
 * Field-level privacy rules:
 *   - `flagsUsed` contains flag NAMES only. Never flag values.
 *   - `gitRepo.owner` + `gitRepo.name` are captured verbatim for product
 *     analytics. Must only be populated when the CLI is run inside a git
 *     repository.
 *   - `instrument` is only present for the `inf instrument` command.
 *
 * All string fields have length caps to prevent a buggy or malicious client
 * from flooding PostHog with oversized payloads.
 */
export const recordCliCommandParams = z.object({
  /** Fully-qualified command name — e.g. "instrument", "training list". */
  command: z.string().min(1).max(120),
  /** `@inference/cli` package version — e.g. "0.0.13". */
  cliVersion: z.string().min(1).max(64),
  /** `process.platform` — e.g. "darwin", "linux", "win32". */
  os: z.string().min(1).max(32),
  /** `process.arch` — e.g. "arm64", "x64". */
  arch: z.string().min(1).max(32),
  /** JavaScript runtime the CLI is running under. */
  runtime: z.object({
    name: z.string().min(1).max(32),
    version: z.string().min(1).max(64),
  }),
  /**
   * Commander flag names that were explicitly set by the user.
   * NEVER include values — flag name only (e.g. "dry-run", "agent").
   */
  flagsUsed: z.array(z.string().min(1).max(64)).max(64).default([]),
  /**
   * Git repository the CLI was invoked inside, parsed from `remote.origin.url`.
   * `null` when the CLI was run outside a git repo, when git is not installed,
   * or when the remote URL could not be parsed.
   */
  gitRepo: z
    .object({
      owner: z.string().min(1).max(200),
      name: z.string().min(1).max(200),
    })
    .nullable()
    .default(null),
  /** Current git branch — `null` when not in a repo or in detached HEAD. */
  gitBranch: z.string().max(200).nullable().default(null),
  /** Enrichment block only populated for the `inf instrument` command. */
  instrument: z
    .object({
      agent: cliInstrumentationAgentSchema.nullable().default(null),
      taskId: z.string().max(120).nullable().default(null),
      dryRun: z.boolean().default(false),
      printPrompt: z.boolean().default(false),
    })
    .nullable()
    .default(null),
});

export type RecordCliCommandParams = z.infer<typeof recordCliCommandParams>;

/**
 * Payload the CLI posts after a successful non-dry-run `inf instrument` agent
 * exit. Intentionally small: no API keys, prompt text, task IDs, code, or
 * file paths — only enough to attribute the completed integration.
 */
export const recordCliInstrumentationCompletedParams = z.object({
  /** `@inference/cli` package version — e.g. "0.0.20". */
  cliVersion: z.string().min(1).max(64),
  /** Catalyst product surface(s) the agent was asked to instrument. */
  mode: cliInstrumentationModeSchema,
  /** Coding agent binary that completed the instrumentation. */
  agent: cliInstrumentationAgentSchema,
  /** Project the instrumentation targeted. */
  projectId: z.string().min(1).max(120),
});

export type RecordCliInstrumentationCompletedParams = z.infer<
  typeof recordCliInstrumentationCompletedParams
>;
